"use client";

import { useMemo, useState } from "react";
import { Input } from "@/components/ui/input";
import { Disclaimer } from "@/components/disclaimer";
import { computeDividendYieldPayout } from "@/lib/calculators/dividend-yield-calculator";
import { formatCurrency } from "@/lib/format";

function parseAmount(draft: string): number {
  const n = Number(draft.replace(",", "."));
  return Number.isFinite(n) ? n : 0;
}

function StatTile({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-lg border border-hairline bg-card p-3 text-center">
      <p className="text-xs text-muted-foreground">{label}</p>
      <p className="mt-1 font-tabular text-lg font-semibold text-ink">{value}</p>
    </div>
  );
}

/**
 * Rein hypothetischer Dividendenrendite-Rechner (backlog.md -> Aktienfinder-Benchmark) -
 * Kapital + angenommene Rendite -> Ausschüttung pro Jahr/Quartal/Monat, ohne Ticker-Bezug
 * und ohne Login. Siehe lib/calculators/dividend-yield-calculator.ts für die Berechnung.
 */
export function DividendYieldCalculator() {
  const [capital, setCapital] = useState("50000");
  const [yieldPercent, setYieldPercent] = useState("4");

  const result = useMemo(
    () => computeDividendYieldPayout({ capital: parseAmount(capital), yieldRate: parseAmount(yieldPercent) / 100 }),
    [capital, yieldPercent],
  );

  return (
    <div className="flex flex-col gap-4 rounded-lg border border-hairline p-4">
      <div>
        <span className="font-serif text-lg font-semibold text-ink">Dividendenrendite-Rechner</span>
        <p className="mt-1 text-xs text-ink-secondary">
          Rein hypothetische Modellrechnung mit einer angenommenen, konstanten
          Dividendenrendite - keine Prognose für eine konkrete Aktie oder einen konkreten
          ETF.
        </p>
      </div>

      <div className="grid grid-cols-2 gap-3">
        <label className="flex flex-col gap-1 text-xs text-muted-foreground">
          Investiertes Kapital (€)
          <Input type="number" min={0} value={capital} onChange={(e) => setCapital(e.target.value)} className="bg-card" />
        </label>
        <label className="flex flex-col gap-1 text-xs text-muted-foreground">
          Angenommene Dividendenrendite (%/Jahr)
          <Input
            type="number"
            min={0}
            value={yieldPercent}
            onChange={(e) => setYieldPercent(e.target.value)}
            className="bg-card"
          />
        </label>
      </div>

      {result && (
        <div className="grid grid-cols-3 gap-3">
          <StatTile label="Pro Jahr" value={formatCurrency(result.annualPayout, "EUR")} />
          <StatTile label="Pro Quartal" value={formatCurrency(result.quarterlyPayout, "EUR")} />
          <StatTile label="Pro Monat" value={formatCurrency(result.monthlyPayout, "EUR")} />
        </div>
      )}

      <p className="text-[0.65rem] text-muted-foreground">
        Brutto vor Steuern (Kapitalertragsteuer/Soli), ohne Berücksichtigung von
        Kursveränderungen, Dividendenwachstum oder -kürzungen.
      </p>
      <Disclaimer />
    </div>
  );
}
